import React from "react";
import styled, { css } from "styled-components";

import {
    bold,
    breakpoints,
    fonts,
    colors,
    easingSeconds,
    focusBorder,
    regular
} from ".";

const ButtonBody = ({
    disabled,
    children,
    className,
    onClick,
    href,
    target
}) => {
    // if a href is passed, it will return an anchor. otherwise, it will return a button
    return href ? (
        <a
            className={className}
            disabled={disabled}
            href={href}
            target={target}
        >
            {children}
        </a>
    ) : (
        <button className={className} disabled={disabled} onClick={onClick}>
            {children}
        </button>
    );
};

const sizes = {
    small: css`
        ${fonts.typeScale[12]}
        line-height: 0.75rem;
        padding-top: 0.5rem;
        padding-bottom: 0.5rem;

        @media (min-width: 48rem) {
            line-height: 0.75rem;
        }
    `,
    medium: css`
        ${fonts.typeScale[14]}
        line-height: 0.875rem;
        letter-spacing: 0.09375rem;
        padding-top: 0.9375rem;
        padding-bottom: 0.9375rem;

        @media (min-width: 48rem) {
            line-height: 0.875rem;
        }
    `,
    large: css`
        ${fonts.typeScale[18]}
        line-height: 1.125rem;
        letter-spacing: 0.125rem;
        padding-top: 1.125rem;
        padding-bottom: 1.125rem;

        @media (min-width: 48rem) {
            font-size: 1.5rem;
            line-height: 1.5rem;
        }
    `
};

export const ButtonStyle = css`
    font-family: "Helvetica Neue LT W05_65 Medium";
    width: ${props => props.width || "100%"};
    color: #ffffff;
    border: 0;
    border-radius: 3px;
    ${({ removeBold }) => !removeBold && bold};
    ${props => sizes[props.size]};
    transition: all ${easingSeconds};
    text-align: center;
    text-decoration: none;

    &:hover {
        cursor: pointer;
    }

    &:disabled {
        background-color: #bababa;
    }

    &:focus {
        outline: solid 3px rgba(62, 169, 245, 0.4);
        outline-offset: 0.25rem;
    }
`;

export const Button = styled(ButtonBody)`
    ${ButtonStyle};
`;

export const BlackButtonStyle = css`
    background-color: #000000;

    &:hover {
        background-color: #404040;
    }

    &:active {
        background-color: #16161d;
    }
`;

export const BlackButton = styled(Button)`
    ${BlackButtonStyle};
`;

export const GrayButtonStyle = css`
    background-color: #404040;

    &:hover {
        background-color: #1f1f1f;
    }

    &:active {
        background-color: #000000;
    }
`;

export const GrayButton = styled(Button)`
    ${GrayButtonStyle};
`;

export const BlueButtonStyle = css`
    background-color: #3ea9f5;

    &:hover {
        background-color: #2e80b7;
    }

    &:active {
        background-color: #1f587f;
    }
`;

export const BlueButton = styled(Button)`
    ${BlueButtonStyle};
`;

// new BlackButton style - see Leadership section on About page for example:
export const NewBlackButton = styled(BlackButton)`
    letter-spacing: 0;
    display: inline-block;
    padding: 1rem; /* 16px */

    /* Mobile: */
    @media (max-width: ${breakpoints.beforeTablet}rem) {
        width: 100%;
    }

    /* Tablet & Desktop: */
    min-width: 12.5rem; /* 200px */
    width: auto;

    &:focus {
        outline: 0;
        ${focusBorder}
    }
`;

export const RoyalBlueButtonStyle = css`
    ${regular}
    letter-spacing: 0;
    font-size: 0.9375rem; /* 15px */
    line-height: 1rem; /* 16px */
    padding: 1rem; /* 16px */
    color: ${colors.emptySearchBG};
    background: ${colors.royalBlue};

    &:hover {
        color: ${colors.emptySearchBG};
        background: ${colors.mutedRoyalBlue};
    }

    &:focus {
        outline: 3px solid ${colors.focus};
    }
`;

export const RoyalBlueButton = styled(Button)`
    ${RoyalBlueButtonStyle}
`;

export const LightBlueButtonStyle = css`
    ${regular}
    letter-spacing: 0;
    font-size: 0.9375rem; /* 15px */
    line-height: 1rem; /* 16px */
    padding: 1rem; /* 16px */
    color: ${colors.dark};
    background: ${colors.softBlue};

    &:hover {
        color: ${colors.emptySearchBG};
        background: ${colors.mutedSoftBlue};
    }

    &:focus {
        outline: 3px solid ${colors.focus};
    }
`;

export const LightBlueButton = styled(Button)`
    ${LightBlueButtonStyle}
`;

export const WhiteOutlineButtonStyle = css`
    /* This button will probably end up being the "small" version when we have time to implement new reusable buttons: */
    ${regular}
    font-size: 0.8125rem; /* 13px */
    line-height: 0.8125rem; /* 13px */
    padding: 0.75rem; /* 12px */
    background: ${colors.whiteDuplicate};
    color: ${colors.dark};
    border: 1px solid ${colors.veryLightPinkTwoDuplicate};

    &:hover {
        color: ${colors.dark};
        background: ${colors.lightGrey};
        border-color: ${colors.veryLightPinkTwo};
    }

    &:focus {
        outline: 3px solid ${colors.focus};
    }
`;

export const WhiteOutlineButton = styled(Button)`
    ${WhiteOutlineButtonStyle};
`;
